import { useCallback, useEffect, useRef, useState } from 'react';
import { useApiClient } from '@/hooks/useApiClient';
import { trGetGaJobStatus, trLaunchGaExperiment } from '../api/ToolRecommenderApiClient';
import type { GaEvaluationInput, GaExperimentResult, GaLaunchStatus } from '../types/ToolRecommenderTypes';

const PENDING_KEY = 'toolRecommender:pendingJob';
const PENDING_TTL_MS = 10 * 60 * 1000; // 10 min
const POLL_INTERVAL_MS = 3000;

interface PendingJob { jobId: string; launchedAt: number }

function loadPendingJob(): PendingJob | null {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const job = JSON.parse(raw) as PendingJob;
    if (Date.now() - job.launchedAt > PENDING_TTL_MS) { sessionStorage.removeItem(PENDING_KEY); return null; }
    return job;
  } catch { return null; }
}

function savePendingJob(jobId: string): void {
  try { sessionStorage.setItem(PENDING_KEY, JSON.stringify({ jobId, launchedAt: Date.now() })); } catch { /* ignore */ }
}

function clearPendingJob(): void {
  try { sessionStorage.removeItem(PENDING_KEY); } catch { /* ignore */ }
}

export function useToolRecommender() {
  const http = useApiClient();
  const pendingOnMount = loadPendingJob();

  const [status, setStatus] = useState<GaLaunchStatus>(pendingOnMount ? 'running' : 'idle');
  const [result, setResult] = useState<GaExperimentResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isResumed, setIsResumed] = useState(!!pendingOnMount);
  const [elapsedSeconds, setElapsedSeconds] = useState(() =>
    pendingOnMount ? Math.floor((Date.now() - pendingOnMount.launchedAt) / 1000) : 0
  );

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelledRef = useRef(false);

  const stopAll = useCallback(() => {
    if (timerRef.current !== null) { clearInterval(timerRef.current); timerRef.current = null; }
    if (pollRef.current !== null) { clearTimeout(pollRef.current); pollRef.current = null; }
  }, []);

  const poll = useCallback((jobId: string) => {
    if (!http || cancelledRef.current) return;
    pollRef.current = setTimeout(async () => {
      if (cancelledRef.current || !http) return;
      const outcome = await trGetGaJobStatus(http, jobId);
      if (cancelledRef.current) return;
      if (outcome.type === 'completed') {
        stopAll(); clearPendingJob();
        setResult(outcome.result); setStatus('completed');
      } else if (outcome.type === 'error') {
        stopAll(); clearPendingJob();
        setError(outcome.message); setStatus('failed');
      } else {
        // still pending/processing — keep polling
        poll(jobId);
      }
    }, POLL_INTERVAL_MS);
  }, [http, stopAll]);

  // Resume polling if there was an in-progress job when the page loaded
  useEffect(() => {
    if (pendingOnMount && http) {
      cancelledRef.current = false;
      timerRef.current = setInterval(() => setElapsedSeconds((s) => s + 1), 1000);
      poll(pendingOnMount.jobId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);  // intentionally runs only on mount

  const launch = useCallback(async (input: GaEvaluationInput) => {
    if (!http) return;
    stopAll();
    cancelledRef.current = false;
    setIsResumed(false);
    setStatus('running'); setResult(null); setError(null); setElapsedSeconds(0);
    timerRef.current = setInterval(() => setElapsedSeconds((s) => s + 1), 1000);

    const outcome = await trLaunchGaExperiment(http, input);

    if (outcome.type === 'completed') {
      stopAll(); clearPendingJob();
      setIsResumed(false); setResult(outcome.result); setStatus('completed');
    } else if (outcome.type === 'error') {
      stopAll(); clearPendingJob();
      setIsResumed(false); setError(outcome.message); setStatus('failed');
    } else {
      // 202 — GA still running; save jobId and start polling
      savePendingJob(outcome.jobId);
      poll(outcome.jobId);
    }
  }, [http, poll, stopAll]);

  const reset = useCallback(() => {
    cancelledRef.current = true;
    stopAll(); clearPendingJob();
    setStatus('idle'); setResult(null); setError(null); setElapsedSeconds(0);
  }, [stopAll]);

  useEffect(() => {
    return () => { cancelledRef.current = true; stopAll(); };
  }, [stopAll]);

  return { status, result, error, elapsedSeconds, isResumed, launch, reset };
}
